import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO, { SITE_URL } from '@/hooks/useSEO';
import { categories, resources } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { products } from '@/mocks/products';

function HeroSection() {
  const { t } = useTranslation('common');
  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="grid grid-cols-2 md:grid-cols-4 h-full">
          <div className="h-full bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Minimalist%20fashion%20product%20photography%20flatlay%20on%20soft%20beige%20linen%20background%20with%20natural%20light%2C%20clean%20editorial%20style%2C%20neutral%20warm%20tones%2C%20high%20detail%20texture&width=600&height=800&seq=hero-bg-1&orientation=portrait)' }} />
          <div className="h-full bg-cover bg-center" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Stylish%20sneakers%20and%20shoes%20arrangement%20on%20light%20concrete%20surface%20with%20soft%20natural%20light%2C%20clean%20product%20photography%2C%20neutral%20warm%20tones%2C%20editorial%20style%2C%20high%20detail&width=600&height=800&seq=hero-bg-2&orientation=portrait)' }} />
          <div className="h-full bg-cover bg-center hidden md:block" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Minimalist%20leather%20accessories%20and%20bags%20on%20light%20wood%20surface%20with%20soft%20natural%20light%2C%20clean%20product%20photography%2C%20neutral%20warm%20tones%2C%20editorial%20style%2C%20high%20detail&width=600&height=800&seq=hero-bg-3&orientation=portrait)' }} />
          <div className="h-full bg-cover bg-center hidden md:block" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20tech%20gadgets%20and%20electronics%20on%20soft%20marble%20surface%20with%20natural%20light%2C%20clean%20product%20photography%2C%20neutral%20warm%20tones%2C%20editorial%20style%2C%20high%20detail&width=600&height=800&seq=hero-bg-4&orientation=portrait)' }} />
        </div>
        <div className="hero-bg-overlay absolute inset-0" />
      </div>

      <div className="container-main relative z-10 pt-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-6">
            <i className="ri-shield-check-line" />
            <span>{t('trustLine')}</span>
          </div>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {t('heroTitle')}
          </h1>
          <p className="font-heading text-xl md:text-2xl text-primary-700 mb-4 italic">
            {t('heroSubtitle')}
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-lg">
            {t('heroDescription')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary gap-2"
            >
              <i className="ri-external-link-line" />
              {t('ctaBuyNow')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-secondary gap-2"
            >
              <i className="ri-arrow-right-line" />
              {t('ctaMainWebsite')}
            </a>
          </div>

          <div className="flex items-center gap-6 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-check-double-line text-accent-500" />
              {t('heroTrustUpdated')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-check-double-line text-accent-500" />
              {t('heroTrustPractical')}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-check-double-line text-accent-500" />
              {t('heroTrustResearch')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  const { t } = useTranslation('common');
  return (
    <section className="section-padding bg-background-100">
      <div className="container-main">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {t('whatIsTitle')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {t('whatIsDesc')}
          </p>
        </div>
      </div>
    </section>
  );
}

function CategoryGridSection() {
  const { t } = useTranslation('common');
  const [showAll, setShowAll] = useState(false);
  const display = showAll ? categories : categories.slice(0, 8);

  return (
    <section className="section-padding bg-background-50">
      <div className="container-main">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
          {t('popularCategories')}
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
          {t('categoryBrowseDesc')}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {display.map((cat) => (
            <a
              key={cat.name}
              href={cat.href}
              target="_blank"
              rel="noopener nofollow"
              className="card card-hover p-4 md:p-5 flex items-center gap-3 group"
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary-100 group-hover:bg-primary-200 transition-colors flex-shrink-0">
                <i className={`${cat.icon} text-primary-600 text-lg`} />
              </div>
              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors">
                {cat.name}
              </span>
            </a>
          ))}
        </div>

        {categories.length > 8 && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAll(!showAll)}
              className="btn-secondary gap-2"
            >
              <i className={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
              {showAll ? t('showLess') : t('showAllCategories')}
            </button>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary gap-2"
          >
            <i className="ri-external-link-line" />
            {t('siteName')}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-secondary gap-2"
          >
            <i className="ri-shopping-bag-line" />
            {t('ctaBuyNow')}
          </a>
        </div>
      </div>
    </section>
  );
}

function ResourceCardsSection() {
  const { t } = useTranslation('common');

  return (
    <section className="section-padding bg-background-100">
      <div className="container-main">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
          {t('exploreResources')}
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
          {t('resourceCardDesc')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {resources.map((res) => (
            <div key={res.key} className="card card-hover p-5 md:p-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-primary-100 mb-4">
                <i className={`${res.icon} text-primary-600 text-xl`} />
              </div>
              <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">
                {t(res.key)}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                {t('resourceCardDesc')} {t(res.key).toLowerCase()}.
              </p>
              <Link to={`/${res.slug}`} className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
                {t('readMore')}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonTableSection() {
  const { t } = useTranslation('common');

  const shippingData = [
    { method: 'Standard Shipping', speed: '12-20 days', cost: 'Low', tracking: 'Basic', bestFor: 'Budget buyers' },
    { method: 'Express Shipping', speed: '7-12 days', cost: 'Medium', tracking: 'Full', bestFor: 'Faster delivery' },
    { method: 'Premium Shipping', speed: '5-8 days', cost: 'High', tracking: 'Full + Insurance', bestFor: 'High-value items' },
  ];

  return (
    <section className="section-padding bg-background-50">
      <div className="container-main">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
          {t('compareTitle')}
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
          {t('compareDesc')}
        </p>

        <div className="overflow-x-auto rounded-lg border border-background-200">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr>
                <th className="spreadsheet-cell-header">{t('shippingMethod')}</th>
                <th className="spreadsheet-cell-header">{t('speed')}</th>
                <th className="spreadsheet-cell-header">{t('cost')}</th>
                <th className="spreadsheet-cell-header">{t('tracking')}</th>
                <th className="spreadsheet-cell-header">{t('bestFor')}</th>
              </tr>
            </thead>
            <tbody>
              {shippingData.map((row, i) => (
                <tr key={i} className="spreadsheet-row">
                  <td className="spreadsheet-cell font-medium text-foreground-800">{row.method}</td>
                  <td className="spreadsheet-cell">{row.speed}</td>
                  <td className="spreadsheet-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      row.cost === 'Low' ? 'bg-accent-100 text-accent-800' :
                      row.cost === 'Medium' ? 'bg-secondary-100 text-secondary-800' :
                      'bg-primary-100 text-primary-800'
                    }`}>
                      {row.cost}
                    </span>
                  </td>
                  <td className="spreadsheet-cell">{row.tracking}</td>
                  <td className="spreadsheet-cell text-foreground-500">{row.bestFor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-foreground-400 mt-3 text-center">
          {t('shippingDisclaimer')}
        </p>
      </div>
    </section>
  );
}

function ArticlesSection() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language || 'en';

  return (
    <section className="section-padding bg-background-100">
      <div className="container-main">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
          {t('latestGuides')}
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
          {t('articlesDesc')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articles.map((article) => (
            <article key={article.slug} className="card card-hover overflow-hidden">
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={article.image}
                  alt={t(article.key)}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2 line-clamp-2">
                  {t(article.key)}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-3">
                  {t(article.descKey)}
                </p>
                <Link
                  to={`/${currentLang}/blog/${article.slug}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {t('readMore')}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedProductsSection() {
  const { t } = useTranslation('common');
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const featured = products.slice(0, 8);

  return (
    <section className="section-padding bg-background-100">
      <div className="container-main">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
          {t('featuredProducts')}
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10">
          {t('featuredProductsDesc')}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {featured.map((product) => (
            <div key={product.id} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <a href={product.url} target="_blank" rel="noopener nofollow" className="block">
                <div className="aspect-square bg-background-100 overflow-hidden relative">
                  {!imgErrors[product.id] ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={() => setImgErrors((prev) => ({ ...prev, [product.id]: true }))}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-background-200">
                      <div className="w-14 h-14 flex items-center justify-center">
                        <i className="ri-shopping-bag-line text-3xl text-secondary-500" />
                      </div>
                    </div>
                  )}
                </div>
              </a>

              <div className="p-3 md:p-4">
                <a href={product.url} target="_blank" rel="noopener nofollow" className="block">
                  <h3 className="text-xs font-medium text-foreground-900 leading-snug line-clamp-2 min-h-[2.4rem] hover:text-primary-600 transition-colors">
                    {product.name}
                  </h3>
                </a>

                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-primary-600">¥{product.priceCNY}</span>
                  <span className="text-xs text-foreground-600">≈ ${product.priceUSD}</span>
                </div>

                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-md text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line" />
                  {t('buyNow')}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-900 rounded-md font-medium hover:bg-background-200 transition-colors border border-background-200 whitespace-nowrap"
          >
            {t('viewAllProducts')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  const { t } = useTranslation('common');

  return (
    <section className="section-padding bg-background-50">
      <div className="container-main">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-accent-100 mx-auto mb-5">
            <i className="ri-shield-check-line text-accent-600 text-2xl" />
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {t('safetyTitle')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {t('safetyDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/safety" className="btn-primary gap-2">
              <i className="ri-shield-check-line" />
              {t('safetyCenter')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-secondary gap-2"
            >
              <i className="ri-external-link-line" />
              {t('ctaMainWebsite')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTASection() {
  const { t } = useTranslation('common');

  return (
    <section className="section-padding bg-primary-500">
      <div className="container-main text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
          {t('finalCTATitle')}
        </h2>
        <p className="text-sm md:text-base text-background-100/80 leading-relaxed mb-6 max-w-xl mx-auto">
          {t('finalCTADesc')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium text-sm bg-background-50 text-primary-700 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-table-line" />
            {t('ctaExplore')}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium text-sm border border-background-50/30 text-background-50 hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-external-link-line" />
            {t('ctaMainWebsite')}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language || 'en';

  useSEO({
    title: t('metaHomeTitle'),
    description: t('metaHomeDesc'),
    canonical: `${SITE_URL}/${currentLang}`,
    lang: currentLang,
    ogType: 'website',
    ogTitle: t('metaHomeTitle'),
    ogDesc: t('metaHomeDesc'),
    ogUrl: `${SITE_URL}/${currentLang}`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'AllChinaBuy Spreadsheet',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  });

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <HeroSection />
        <WhatIsSection />
        <CategoryGridSection />
        <ResourceCardsSection />
        <ComparisonTableSection />
        <ArticlesSection />
        <FeaturedProductsSection />
        <SafetySection />
        <FinalCTASection />
      </main>

      <Footer />
    </div>
  );
}